import { Text, InlineText } from '@hg/stylizator/typography'
import { desktop, Spacing } from '@hg/stylizator/utils'
import { HTMLReactParserOptions, Element, attributesToProps, domToReact } from 'html-react-parser'
import * as React from 'react'
import styled from 'styled-components'

import ClientOnly from 'app/components/ClientOnly'
import { useTranslation } from 'app/hooks/use-translation'

export const TableOfContents = styled.div`
  overflow: auto;

  & ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  & li {
    border-left: 3px solid ${(props) => props.theme.color.blue[20]};
    padding: 8px 16px;
    color: ${(props) => props.theme.color.gray[90]} !important;

    &:hover {
      color: ${(props) => props.theme.color.blue[50]} !important;
    }

    &.read {
      border-color: ${(props) => props.theme.color.blue[50]};
    }

    &.active {
      font-weight: 700;
    }
  }

  & a {
    text-decoration: none !important;
    color: currentColor !important;
    font-weight: inherit !important;
  }
`

export const TableOfContentsMobile = styled(TableOfContents)`
  background-color: ${(props) => props.theme.color.blue[10]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 40px !important;

  @media ${desktop} {
    display: none !important;
  }
`

const TocStripMobile: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const t = useTranslation()

  return (
    <ClientOnly>
      <TableOfContentsMobile id="toc-mobile">
        <Spacing bottom={2} horizontal={1}>
          <Text type="subheading">
            <p style={{ textTransform: 'uppercase' }}>{t('on_this_article')}</p>
          </Text>
        </Spacing>
        {children}
      </TableOfContentsMobile>
    </ClientOnly>
  )
}

export const tocStripMobileOptions: HTMLReactParserOptions = {
  trim: true,
  replace: (domNode) => {
    if (domNode instanceof Element && domNode.attribs) {
      const props = attributesToProps(domNode.attribs)

      if (props.id === 'ez-toc-container') {
        const filteredChildren = domNode.childNodes.filter((it) => it instanceof Element && it.name === 'nav')
        const children = domToReact(filteredChildren, tocOptions)
        return <TocStripMobile>{children}</TocStripMobile>
      }
    }

    return domNode
  },
}

const TocStrip: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const t = useTranslation()

  return (
    <TableOfContents id="toc">
      <Spacing vertical={2} horizontal={1}>
        <Text type="subheading">
          <p style={{ textTransform: 'uppercase' }}>{t('on_this_article')}</p>
        </Text>
      </Spacing>
      {children}
    </TableOfContents>
  )
}

export const tocStripOptions: HTMLReactParserOptions = {
  trim: true,
  replace: (domNode) => {
    if (domNode instanceof Element && domNode.attribs) {
      if (domNode.name === 'div') {
        const filteredChildren = domNode.childNodes.filter((it) => it instanceof Element && it.name === 'nav')
        const children = domToReact(filteredChildren, tocOptions)
        return <TocStrip>{children}</TocStrip>
      }
    }

    return domNode
  },
}

const tocOptions: HTMLReactParserOptions = {
  trim: true,
  replace: (domNode) => {
    if (domNode instanceof Element && domNode.attribs) {
      if (domNode.name === 'li') {
        const props = attributesToProps(domNode.attribs)
        const children = domToReact(domNode.children, tocOptions)

        return (
          <InlineText type="body">
            <span>
              <li {...props}>{children}</li>
            </span>
          </InlineText>
        )
      }

      if (domNode.name === 'a') {
        const props = attributesToProps(domNode.attribs)
        const children = domToReact(domNode.children, tocOptions)

        return (
          <a
            {...props}
            onClick={(event) => {
              event.preventDefault()
              const element = document.querySelector(`[id='${props.href.substring(1)}']`) as HTMLElement | undefined

              if (element) {
                element.style.scrollMarginTop = '70px'
                element.scrollIntoView({ behavior: 'smooth' })
                history.replaceState(
                  'data',
                  'title',
                  location.origin + location.pathname + location.search + props.href,
                )
              }
            }}
          >
            {children}
          </a>
        )
      }

      return domNode
    }

    return domNode
  },
}
